import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConversationService } from "@/services/conversation/ConversationService";
import { Conversation, ConversationListCache, InboxQueue } from "@/types/conversation/conversation";
import { LeadInfoField } from "@/types/leads/lead-info";

export const useConversations = (
  agentIds: string[],
  timeRange: string,
  excludeMailbox = false,
  filters: { conversationType?: string; channel?: string; ownerId?: string; queue?: InboxQueue; viewId?: string } = {},
  /**
   * Pass `enabled: false` while `agentIds` is still being worked out, so no request goes out for a half-built list.
   * `withCounts` puts the exact match and unreviewed totals on the first page.
   */
  options: { enabled?: boolean; withCounts?: boolean } = {},
) => {
  const { conversationType, channel, ownerId, viewId } = filters;
  const { withCounts } = options;
  const queue = filters.queue === "all" ? undefined : filters.queue;
  return useInfiniteQuery({
    enabled: options.enabled ?? true,
    queryKey: ["conversations", agentIds, timeRange, excludeMailbox, conversationType, channel, ownerId, queue, viewId, withCounts],
    queryFn: async ({ pageParam }) => {
      return await ConversationService.getAll({
        agentIds,
        timeRange,
        cursor: pageParam,
        limit: 30,
        excludeMailbox,
        conversationType,
        channel,
        ownerId,
        queue,
        viewId,
        withCounts: withCounts && !pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useConversationById = (id: string | null) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["conversation", id],
    queryFn: () => ConversationService.getById(id!),
    enabled: id !== null,
    staleTime: 60 * 1000,
    initialData: () => {
      if (id === null) return undefined;
      // The inbox already fetched complete conversation records. Reuse them so
      // opening the same conversation from a View is immediate too.
      for (const [, cached] of queryClient.getQueriesData<ConversationListCache>({ queryKey: ["conversations"] })) {
        const conversation = cached?.pages?.flatMap((page) => page.data)
          .find((item) => String(item.id) === id);
        if (conversation) return conversation;
      }
      return undefined;
    },
  });
};

export const useShowConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversation: Conversation) => ConversationService.show(conversation),
    onSuccess: ({ conversation }) => {
      queryClient.setQueryData(["conversation", conversation.id], conversation);
    },
  });
};

export const useMarkAsViewed = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversation: Conversation) => ConversationService.markAsViewed(conversation),
    onSuccess: (_, conversation) => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation", conversation.id] });
    },
  });
};

export const useMarkAllViewed = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => ConversationService.markAllViewed(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
};

export const useDeleteConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversation: Conversation) => ConversationService.delete(conversation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
};

export const useAnalyzeConversation = () => {
  return useMutation({
    mutationFn: ({
      conversation,
      fields,
      log,
    }: {
      conversation: Conversation;
      fields?: LeadInfoField[];
      log?: boolean;
    }) => ConversationService.analyze(conversation, fields, log),
  });
};
